import AuthLayout from '../components/AuthLayout';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Target, User, Mail, Lock } from 'lucide-react';
import GoogleSignInButton, { GOOGLE_CLIENT_ID } from '../components/GoogleSignInButton';
import { trackEvent } from '../utils/analytics';
import { HANDICAP_INDEX_ERROR, MAX_HANDICAP_INDEX, parseHandicapIndex } from '../utils/handicapIndex';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [handicap, setHandicap] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    let handicapIndex: number | undefined;
    if (handicap) {
      const parsedHandicapIndex = parseHandicapIndex(handicap);
      if (parsedHandicapIndex === null) {
        setError(HANDICAP_INDEX_ERROR);
        return;
      }
      handicapIndex = parsedHandicapIndex;
    }
    setIsLoading(true);

    try {
      await register({
        email,
        password,
        full_name: fullName,
        handicap: handicapIndex,
      });
      trackEvent('sign_up', { method: 'email' });
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    'w-full pl-11 pr-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:bg-white/15 focus:ring-2 focus:ring-yellow-500/70 focus:border-transparent outline-none transition';
  const iconClass =
    'absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-300/80 pointer-events-none';

  return (
    <AuthLayout register>
          {/* Google sign-up (shown only when configured) */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div className="mb-4">
                <GoogleSignInButton text="signup_with" onError={setError} />
              </div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-1 h-px bg-green-800/60" />
                <span className="text-xs text-green-400/80">or sign up with email</span>
                <div className="flex-1 h-px bg-green-800/60" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div role="alert" className="bg-red-500/15 text-red-300 border border-red-500/30 p-3 rounded-xl text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-green-200 mb-1">
                Full Name
              </label>
              <div className="relative">
                <User className={iconClass} />
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  className={inputClass}
                  placeholder="John Smith"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-green-200 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className={iconClass} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className={inputClass}
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-green-200 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className={iconClass} />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <div>
              <label htmlFor="handicap" className="block text-sm font-medium text-green-200 mb-1">
                Handicap Index (optional)
              </label>
              <div className="relative">
                <Target className={iconClass} />
                <input
                  id="handicap"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  max={MAX_HANDICAP_INDEX}
                  value={handicap}
                  onChange={(e) => setHandicap(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 11.1"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3.5 rounded-xl font-semibold hover:from-green-500 hover:to-emerald-500 transition active:scale-[0.98] disabled:opacity-50 shadow-lg shadow-green-900/50"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>

            <p className="text-xs text-center text-green-300/70 leading-relaxed">
              By creating an account you confirm you are 18 or older and agree to our{' '}
              <Link to="/terms" className="text-yellow-400/90 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-yellow-400/90 hover:underline">Privacy Policy</Link>.
            </p>
          </form>

          <p className="text-center mt-4 text-sm text-green-200/80">
            Already have an account?{' '}
            <Link to="/login" className="text-yellow-400 font-semibold hover:underline">
              Sign in
            </Link>
          </p>

    </AuthLayout>
  );
};

export default Register;
